import {
    HandlerWithResult,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import {
    chain,
    ProcessFileParameter,
    ProcessFileResult,
    ProcessUploadedFile as DefaultProcessUploadedFile,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import { UploadedItem } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { WorkBook } from "xlsx";

type HandlerType = HandlerWithResult<ProcessFileParameter, ProcessFileResult>;
type UploadRowValue = string | number | null | undefined;
type UploadRow = UploadRowValue[];
type UploadedItemWithSelections = UploadedItem & { mgbwSelectionsInput?: Record<string, string> };

const optionAndValueIdFromCellRegex = /\(ID:([^:()]+):([^()]+)\)\s*$/i;

const fixData = (data: ArrayBuffer) => {
    let output = "";
    let index = 0;
    const chunkSize = 10240;

    for (; index < data.byteLength / chunkSize; ++index) {
        output += String.fromCharCode.apply(null, new Uint8Array(data.slice(index * chunkSize, index * chunkSize + chunkSize)) as any);
    }

    output += String.fromCharCode.apply(null, new Uint8Array(data.slice(index * chunkSize)) as any);
    return output;
};

const limitExceeded = (rowsCount: number, result: ProcessFileResult) => {
    const maximumAllowedRowsCount = 500;
    result.uploadLimitExceeded = rowsCount > maximumAllowedRowsCount;
    return result.uploadLimitExceeded;
};

const parseSelectionsFromRow = (row: UploadRow, headers?: UploadRow): Record<string, string> => {
    const selections: Record<string, string> = {};

    for (let columnIndex = 3; columnIndex < row.length; columnIndex++) {
        const rawCell = row[columnIndex];
        if (rawCell == null) {
            continue;
        }

        const cellValue = rawCell.toString().trim();
        if (!cellValue) {
            continue;
        }

        const fullMatch = cellValue.match(optionAndValueIdFromCellRegex);
        if (fullMatch) {
            const optionId = fullMatch[1]?.trim();
            const valueId = fullMatch[2]?.trim();
            if (optionId && valueId) {
                selections[optionId] = valueId;
            }
            continue;
        }
    }

    return selections;
};

const createUploadedItem = (row: UploadRow, headers?: UploadRow): UploadedItemWithSelections | null => {
    const nameCell = row[0];
    if (nameCell == null || nameCell.toString().length === 0) {
        return null;
    }

    const uploadedItem: UploadedItemWithSelections = {
        name: nameCell.toString(),
    };

    if (row[1]) {
        uploadedItem.qtyOrdered = parseFloat(row[1].toString()) ? parseFloat(row[1].toString()) : undefined;
    }

    if (row[2]) {
        uploadedItem.unitOfMeasure = row[2].toString();
    }

    const selections = parseSelectionsFromRow(row, headers);
    if (Object.keys(selections).length > 0) {
        uploadedItem.mgbwSelectionsInput = selections;
    }

    return uploadedItem;
};

const processWorkBook = async (workBook: WorkBook, firstRowHeading: boolean, result: ProcessFileResult) => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

    result.uploadedItems = [];
    workBook.SheetNames.forEach((sheetName: string) => {
        const options = { header: 1 };
        let rows = XLSX.utils.sheet_to_json<UploadRow>(workBook.Sheets[sheetName], options);
        if (rows.length === 0) {
            return;
        }

        const headers = firstRowHeading ? rows[0] : undefined;
        if (firstRowHeading) {
            rows = rows.slice(1, rows.length);
        }

        rows = rows.filter(row => row[0] != null && row[0].toString().length > 0);
        if (limitExceeded(rows.length, result)) {
            return;
        }

        result.uploadedItems = rows
            .map(row => createUploadedItem(row, headers))
            .filter((item): item is UploadedItemWithSelections => item !== null);
    });
};

const processCsv = async (data: string, firstRowHeading: boolean, result: ProcessFileResult) => {
    const { parse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");

    result.uploadedItems = [];
    const newLineIndex = data.lastIndexOf("\r\n");
    if (newLineIndex + 2 === data.length) {
        data = data.substr(0, newLineIndex);
    }

    const parseResult = parse<UploadRow>(data);
    if (parseResult.errors.length > 0) {
        result.isBadFile = true;
        return;
    }

    let rows = parseResult.data;
    const headers = firstRowHeading && rows.length > 0 ? rows[0] : undefined;
    if (firstRowHeading) {
        rows = rows.slice(1, rows.length);
    }

    if (limitExceeded(rows.length, result)) {
        return;
    }

    rows.forEach(row => {
        const item = createUploadedItem(row, headers);
        if (item) {
            result.uploadedItems.push(item);
        }
    });
};

export const ProcessUploadedFileExtended: HandlerType = async props => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

    const data = fixData(props.parameter.data);
    try {
        if (props.parameter.fileExtension === "xls" || props.parameter.fileExtension === "xlsx") {
            const workbook = XLSX.read(btoa(data), { type: "base64" });
            if (workbook) {
                await processWorkBook(workbook, props.parameter.firstRowHeading, props.result);
            }
        } else if (props.parameter.fileExtension === "csv") {
            await processCsv(data, props.parameter.firstRowHeading, props.result);
        } else {
            props.result.isBadFile = true;
        }
    } catch (error) {
        logger.error(error);
        props.result.isBadFile = true;
    }

    if (!props.result.isBadFile && !props.result.uploadLimitExceeded) {
        props.result.allowCancel = true;
        props.result.isUploading = true;
    }
};

replaceInChain(chain, DefaultProcessUploadedFile, ProcessUploadedFileExtended);
